import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  FightCard, SlotArt, SlotSearch, Btn, btn, field, eyebrow, panel, LiveDot, Multi,
} from './BattleParts';
import {
  C, FORMAT_META, FORMATS, isLive, adminBattle, battleAction, fmtAmount, roundLabel, sidesOf, ordinal,
  focusedMatch, EXIT_COMMAND, useServerClock, isThirdPlace, playersInPlay, sideScore, fightLabels,
  tournamentLabels,
} from './battleLib';

// ============================================
// Admin controls for slot battles, inline on /slot-battles (the whole show
// runs from one tab while live — this page is often on stream itself).
// Everything here is admin-only on the server; this only decides what renders.
// ============================================

function useAct(onDone) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const act = useCallback(async (path, body, method = 'POST') => {
    setBusy(true);
    setError(null);
    try {
      const res = await battleAction(path, body, method);
      if (onDone) onDone();
      return res ?? true;
    } catch (e) {
      setError(e.message);
      if (onDone) onDone();
      return null;
    } finally {
      setBusy(false);
    }
  }, [onDone]);
  return { busy, error, setError, act };
}

const SmallBtn = ({ active, style, ...p }) => (
  <button
    type="button"
    className="sb-btn"
    {...p}
    style={{ ...btn(active ? 'on' : 'ghost', 'sm'), ...style }}
  />
);

const Choice = ({ on, children, ...p }) => (
  <button
    type="button"
    {...p}
    style={{
      padding: '0.4rem 0.85rem', borderRadius: '9px', cursor: 'pointer', fontFamily: C.font, fontSize: '0.8rem', fontWeight: 700,
      border: `1px solid ${on ? 'rgba(168,85,247,0.6)' : 'rgba(255,255,255,0.12)'}`, background: on ? 'rgba(168,85,247,0.12)' : 'transparent',
      color: on ? '#fff' : C.muted,
    }}
  >
    {children}
  </button>
);

const Label = ({ children, style }) => <span style={{ ...eyebrow, display: 'block', marginBottom: '0.35rem', ...style }}>{children}</span>;
const Hint = ({ children, style }) => <p style={{ margin: '0.4rem 0 0', fontSize: '0.7rem', color: C.dim, lineHeight: 1.45, ...style }}>{children}</p>;
const ErrorBox = ({ children }) => (
  <p style={{ margin: '0.75rem 0 0', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.1)', padding: '0.6rem 0.8rem', fontSize: '0.82rem', color: '#fca5a5' }}>{children}</p>
);

// The OBS widget's URL. Hidden until clicked — this page is often on stream
// itself, and a visible overlay link gets copied into chat.
const WIDGET_PREVIEWS = [
  ['standby', 'Stand-by'], ['lobby', 'Join screen'], ['roll', 'Roll'], ['fight', 'Bonus landing'], ['result', 'Knockout'],
  ['arena-result', '3v3 result'], ['arena-2v2-result', '2v2 result'], ['versus-result', 'Vs chat'], ['versus-2v2', 'Vs 2v2'],
  ['picking', 'Pick window'], ['bracket', 'Round board'], ['third-place', '3rd place match'], ['final-result', 'Champion'],
  ['podium', 'Podium'], ['summary', 'Session over'],
];

function StreamWidgetCard() {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/widget/battles`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked — the URL is on screen to select */ }
  };
  return (
    <div style={{ marginTop: '1rem', borderRadius: '14px', border: `1px solid ${C.line}`, background: 'rgba(0,0,0,0.3)', padding: '1rem' }}>
      <Label>stream widget</Label>
      <Hint style={{ marginTop: 0 }}>
        One OBS browser source for every battle — it follows these controls and shows a stand-by screen between battles.
      </Hint>
      <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
        <code style={{ flex: 1, minWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderRadius: '9px', border: `1px solid ${C.line}`, background: 'rgba(255,255,255,0.04)', padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: C.lavLight, userSelect: 'all' }}>{url}</code>
        <Btn onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</Btn>
        <a href={url} target="_blank" rel="noreferrer noopener" style={{ ...btn('ghost'), textDecoration: 'none' }}>Open</a>
      </div>
      <Hint>
        OBS → Sources → <strong style={{ color: '#fff' }}>+ Browser</strong> → paste the URL, width <strong style={{ color: '#fff' }}>1600</strong>, height{' '}
        <strong style={{ color: '#fff' }}>900</strong>. The widget draws itself at whatever 16:9 size the source is, so set it big and resize it on the
        canvas rather than stretching a small one. Background is transparent.
      </Hint>
      <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
        <span style={{ fontSize: '0.7rem', color: C.dim }}>Preview:</span>
        {WIDGET_PREVIEWS.map(([k, label]) => (
          <a key={k} href={`${url}?demo=${k}`} target="_blank" rel="noreferrer noopener" style={{ ...btn('ghost', 'sm'), textDecoration: 'none', fontWeight: 600 }}>{label}</a>
        ))}
      </div>
    </div>
  );
}

// ---------- create ----------

function PrizeInputs({ prizes, setPrizes, max }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.5rem' }}>
      {prizes.map((p, i) => (
        <label key={i} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', width: '90px' }}>
          <span style={{ ...eyebrow, fontSize: '0.56rem' }}>{ordinal(i + 1)}</span>
          <input type="number" min="0" value={p} placeholder="$" style={field}
            onChange={(e) => setPrizes(prizes.map((x, j) => (j === i ? e.target.value : x)))} />
        </label>
      ))}
      {prizes.length < max && <SmallBtn onClick={() => setPrizes([...prizes, ''])}>+ place</SmallBtn>}
      {prizes.length > 1 && <SmallBtn onClick={() => setPrizes(prizes.slice(0, -1))}>− place</SmallBtn>}
    </div>
  );
}

// "Site accounts only": everyone rolled has logged in on snutzy.gg at least
// once (so a prize has a Shuffle username to go to). The pool itself still
// takes everyone — this only narrows who a roll can land on.
function SiteOnlyToggle({ value, onChange }) {
  return (
    <div style={{ marginTop: '1rem' }}>
      <Label>who can be rolled</Label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {[[false, 'Everyone in chat'], [true, 'Site accounts only']].map(([k, label]) => (
          <Choice key={label} on={value === k} onClick={() => onChange(k)}>{label}</Choice>
        ))}
      </div>
      <Hint>
        {value
          ? 'Rolls skip anyone who has never logged in on snutzy.gg with Twitch — every rolled player has an account (and usually a Shuffle username) to pay.'
          : 'Anyone who typed the command can be rolled. Chat-giveaway shadowbans are never rolled either way.'}
      </Hint>
    </div>
  );
}

function SiteOnlySelect({ battle, act, busy }) {
  return (
    <select
      value={battle.site_only ? 'site' : 'all'}
      disabled={busy}
      onChange={(e) => act(`/api/admin/battles/${battle.id}`, { site_only: e.target.value === 'site' }, 'PUT')}
      title="Who a roll can land on"
      style={{ ...field, width: 'auto', padding: '0.45rem 0.6rem' }}
    >
      <option value="all">Roll: everyone in chat</option>
      <option value="site">Roll: site accounts only</option>
    </select>
  );
}

// Rolled players' site accounts (admin route only): whether they have one and
// the Shuffle username their prize goes to.
const BattleAccountsContext = createContext({});
// the entries that can still be picked (admin payload's eligible_entries) —
// what "Swap teammate" chooses from
const BattlePoolContext = createContext([]);

function PlayerAccount({ playerId }) {
  const acc = useContext(BattleAccountsContext)[playerId];
  if (!playerId || !acc) return null;
  return (
    <span style={{ fontSize: '0.62rem', color: acc.shuffle ? C.lavLight : acc.site ? C.muted : C.loss }}>
      {acc.shuffle ? `Shuffle: ${acc.shuffle}` : acc.site ? 'site account · no Shuffle linked' : 'no site account'}
    </span>
  );
}

function CreateBattle({ onDone, defaultFormat }) {
  const [format, setFormat] = useState(defaultFormat || 'tournament');
  const [f, setF] = useState({
    command: '!join', title: '', default_buy: '', bracket_size: 16, repick: 'each_round', streamer_name: 'Snutzy',
    default_team_size: 1, default_prize: '', third_place: true, site_only: false,
  });
  const [prizes, setPrizes] = useState(['', '', '']);
  const { busy, error, act } = useAct(onDone);
  useEffect(() => { if (defaultFormat) setFormat(defaultFormat); }, [defaultFormat]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  return (
    <div style={{ marginTop: '1rem' }}>
      <div className="sb-grid sb-3">
        {FORMATS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setFormat(k)}
            style={{
              textAlign: 'left', padding: '0.8rem', borderRadius: '12px', cursor: 'pointer', fontFamily: C.font,
              border: `1px solid ${format === k ? 'rgba(168,85,247,0.6)' : C.line}`, background: format === k ? 'rgba(168,85,247,0.07)' : 'transparent',
            }}
          >
            <span style={{ display: 'block', fontSize: '0.88rem', fontWeight: 800, color: '#fff' }}>{FORMAT_META[k].icon} {FORMAT_META[k].label}</span>
            <span style={{ display: 'block', marginTop: '0.3rem', fontSize: '0.7rem', lineHeight: 1.4, color: C.muted }}>{FORMAT_META[k].blurb}</span>
          </button>
        ))}
      </div>

      <div className="sb-grid sb-3" style={{ marginTop: '1rem' }}>
        <label><Label>chat command</Label><input value={f.command} onChange={set('command')} style={field} /></label>
        <label><Label>title (optional)</Label><input value={f.title} onChange={set('title')} placeholder="Friday cup" style={field} /></label>
        <label><Label>usual buy ($)</Label><input type="number" min="0" value={f.default_buy} onChange={set('default_buy')} placeholder="prefills results" style={field} /></label>
      </div>
      <Hint>
        Default <strong style={{ color: '#fff' }}>!join</strong> keeps the viewer bonus hunt&apos;s <strong style={{ color: '#fff' }}>!slot</strong> free. Use !slot if you prefer — then
        a viewer hunt can&apos;t run at the same time.
      </Hint>

      {format === 'tournament' && (
        <>
          <div style={{ marginTop: '1rem' }}>
            <Label>bracket size</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {[4, 8, 16, 32, 64].map((n) => (
                <Choice key={n} on={f.bracket_size === n} onClick={() => setF((x) => ({ ...x, bracket_size: n }))}>
                  {n} <span style={{ fontSize: '0.64rem', color: C.dim }}>· {Math.log2(n)} rounds</span>
                </Choice>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <Label>slot picks</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {[['each_round', 'New pick every round'], ['keep', 'Same slot all the way']].map(([k, label]) => (
                <Choice key={k} on={f.repick === k} onClick={() => setF((x) => ({ ...x, repick: k }))}>{label}</Choice>
              ))}
            </div>
            {f.repick === 'each_round' && (
              <Hint>Between rounds the widget shows who still owes a pick; survivors type {f.command || '!join'} &lt;slot&gt; again. Round winners can pick early.</Hint>
            )}
          </div>
          <div style={{ marginTop: '1rem' }}>
            <Label>3rd place</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {[[true, '3rd place match'], [false, 'By the semi-final multi']].map(([k, label]) => (
                <Choice key={label} on={f.third_place === k} onClick={() => setF((x) => ({ ...x, third_place: k }))}>{label}</Choice>
              ))}
            </div>
            <Hint>
              {f.third_place
                ? 'The two semi-final losers play one more match for 3rd — play it before the final.'
                : 'No extra match — the semi-final loser with the higher multi takes 3rd.'}
            </Hint>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <Label>prizes by placement</Label>
            <Hint style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              {f.third_place ? 'Places after 4th' : 'Places after 2nd'} are ranked by the multi each player went out with — no extra buys.
            </Hint>
            <PrizeInputs prizes={prizes} setPrizes={setPrizes} max={f.bracket_size} />
          </div>
        </>
      )}
      {format !== 'tournament' && (
        <>
          <div style={{ marginTop: '1rem' }}>
            <Label>fight size</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {[1, 2, 3].map((n) => (
                <Choice key={n} on={f.default_team_size === n} onClick={() => setF((x) => ({ ...x, default_team_size: n }))}>
                  {n}v{n}{' '}
                  <span style={{ fontSize: '0.64rem', color: C.dim }}>
                    · {format === 'versus' ? (n === 1 ? '1 viewer' : `you + ${n - 1}, vs ${n}`) : `${n * 2} players`}
                  </span>
                </Choice>
              ))}
            </div>
            <Hint>
              {format === 'versus'
                ? "The default for every fight — change it per fight when you roll. In a 2v2/3v3 you pick your teammates from the pool; chat's side is rolled."
                : 'The default for every fight — you can still change it per fight when you roll.'}
            </Hint>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
            <label style={{ width: '170px' }}>
              <Label>prize per fight ($)</Label>
              <input type="number" min="0" value={f.default_prize} onChange={set('default_prize')} placeholder="0 = no prize" style={field} />
            </label>
            {format === 'versus' && (
              <label style={{ width: '220px' }}>
                <Label>house side is called</Label>
                <input value={f.streamer_name} onChange={set('streamer_name')} style={field} />
              </label>
            )}
          </div>
          <Hint>
            {format === 'arena'
              ? 'Paid to the winning team, split equally. Shown on the join screen; editable per fight.'
              : "Paid only when chat beats you — split equally across chat's team in a 2v2/3v3. When your side wins nobody is paid, your teammates included. Shown on the join screen; editable per fight."}
          </Hint>
        </>
      )}

      <SiteOnlyToggle value={f.site_only} onChange={(v) => setF((x) => ({ ...x, site_only: v }))} />

      {error && <ErrorBox>{error}</ErrorBox>}
      <Btn
        kind="primary"
        size="lg"
        style={{ marginTop: '1.25rem' }}
        disabled={busy}
        onClick={() => act('/api/admin/battles', {
          format, ...f,
          prizes: format === 'tournament' ? prizes.map((p) => Number(p) || 0) : undefined,
          default_prize: format === 'tournament' ? undefined : f.default_prize,
          default_team_size: format === 'tournament' ? undefined : f.default_team_size,
        })}
      >
        Open {FORMAT_META[format].label.toLowerCase()} · entries on
      </Btn>
    </div>
  );
}

// ---------- per-play controls ----------

function PlayControls({ play, match, battle, act, busy }) {
  const [buy, setBuy] = useState(battle.default_buy ?? '');
  const [payout, setPayout] = useState('');
  const [editing, setEditing] = useState(false);
  const [remember, setRemember] = useState(true);
  const [swapping, setSwapping] = useState(false);
  const pool = useContext(BattlePoolContext);
  // a versus teammate is the streamer's choice, so he can choose the
  // replacement too (Replace (AFK) still rolls a random one)
  const teammate = battle.format === 'versus' && play.side === 'a' && !!play.player_id;
  const pickLocked = play.buy != null;
  // Per player, not per fight: a 3v3's bonuses are bought one at a time, and
  // an AFK teammate further down must still be swappable after the first
  // results are in. Same rule the replace route enforces.
  const canReplace = play.player_id && play.buy == null && match.status !== 'done' && battle.status === 'live'
    && (battle.format !== 'tournament' || match.round === 1);

  const submit = async (e) => {
    e.preventDefault();
    const ok = await act(`/api/admin/battles/plays/${play.id}/result`, { buy, payout });
    if (ok) setPayout('');
  };
  const small = { ...field, padding: '0.32rem 0.45rem', fontSize: '0.74rem' };

  return (
    <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
      <PlayerAccount playerId={play.player_id} />
      {play.buy == null ? (
        <form onSubmit={submit} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.35rem' }}>
          <label style={{ width: '78px' }}>
            <span style={{ ...eyebrow, fontSize: '0.52rem' }}>buy</span>
            <input type="number" min="0" step="0.01" value={buy} onChange={(e) => setBuy(e.target.value)} style={small} />
          </label>
          <label style={{ width: '92px' }}>
            <span style={{ ...eyebrow, fontSize: '0.52rem' }}>payout</span>
            <input type="number" min="0" step="0.01" value={payout} onChange={(e) => setPayout(e.target.value)} style={small} />
          </label>
          <button type="submit" className="sb-btn" disabled={busy || buy === '' || payout === ''} style={btn('primary', 'sm')}>💥 Enter</button>
        </form>
      ) : (
        <div>
          <SmallBtn disabled={busy} onClick={() => act(`/api/admin/battles/plays/${play.id}/result`, { clear: true })}>Clear result</SmallBtn>
        </div>
      )}
      {!pickLocked && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
          <SmallBtn onClick={() => setEditing((v) => !v)}>{editing ? 'Close' : play.slot ? 'Change slot' : 'Set slot'}</SmallBtn>
          {canReplace && teammate && (
            <SmallBtn active={swapping} onClick={() => setSwapping((v) => !v)}>{swapping ? 'Close' : 'Swap teammate'}</SmallBtn>
          )}
          {canReplace && (
            <SmallBtn disabled={busy} onClick={() => act(`/api/admin/battles/players/${play.player_id}/replace`)} title="Swap for a random entry from the pool (AFK)">
              Replace (AFK)
            </SmallBtn>
          )}
        </div>
      )}
      {swapping && canReplace && (
        <TeammatePicker
          entries={pool}
          chosen={[]}
          seats={1}
          streamer={battle.streamer_name}
          onChange={async ([e]) => {
            if (!e) return;
            const ok = await act(`/api/admin/battles/players/${play.player_id}/replace`, { entry_id: e.id });
            if (ok) setSwapping(false);
          }}
        />
      )}
      {editing && !pickLocked && (
        <div style={{ borderRadius: '10px', border: `1px solid ${C.line}`, background: 'rgba(0,0,0,0.35)', padding: '0.5rem' }}>
          <SlotSearch
            autoFocus
            onPick={async ({ slot, text }) => {
              const ok = await act(`/api/admin/battles/plays/${play.id}/pick`, slot
                ? { slot_key: slot.key, remember: remember && !!play.slot?.raw && !play.is_streamer }
                : { text });
              if (ok) setEditing(false);
            }}
          />
          {play.slot?.raw && !play.is_streamer && (
            <label style={{ marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: C.muted }}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              remember “{play.slot.raw}” means this slot next time
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// A match folded to one line: who, on what, how it stands, and the two
// things you do from here — put it on stream, or open it. Only the match on
// stream (and any you open) is a full card.
function MatchRow({ match, battle, labels, heading, act, busy, onOpen }) {
  const { a, b } = sidesOf(match);
  const done = match.status === 'done';
  const both = a.length && b.length;
  const entered = match.plays.filter((p) => p.buy != null).length;
  const side = (key, plays) => {
    const won = done && match.winner_side === key;
    const score = done ? match[`${key}_score`] : sideScore(plays);
    const one = plays.length === 1 ? plays[0] : null;
    return (
      <span style={{ display: 'flex', minWidth: 0, flex: 1, alignItems: 'center', gap: '0.5rem', flexDirection: key === 'b' ? 'row-reverse' : 'row', textAlign: key === 'b' ? 'right' : 'left', opacity: done && !won ? 0.5 : 1 }}>
        {one && <SlotArt slot={one.slot} width={24} radius={4} />}
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '0.84rem', fontWeight: won ? 900 : 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{labels[key]}</span>
          {one && <span style={{ display: 'block', fontSize: '0.66rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{one.slot?.name || one.slot?.raw || 'no pick'}</span>}
        </span>
        {score != null && <Multi value={score} size="0.84rem" />}
      </span>
    );
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 0.75rem', borderRadius: '12px', border: `1px solid ${C.line}`, background: 'rgba(0,0,0,0.22)', padding: '0.5rem 0.75rem' }}>
      <span style={{ ...eyebrow, fontSize: '0.56rem', width: '8rem', flexShrink: 0 }}>{heading}</span>
      <div style={{ display: 'flex', minWidth: '14rem', flex: 1, alignItems: 'center', gap: '0.75rem' }}>
        {side('a', a)}
        <span style={{ fontSize: '0.7rem', fontWeight: 900, color: C.violet }}>◆</span>
        {side('b', b)}
      </div>
      <span style={{ ...eyebrow, fontSize: '0.56rem', flexShrink: 0 }}>
        {done ? (match.forfeit ? 'forfeit' : 'decided') : both ? `${entered}/${match.plays.length} in` : 'waiting'}
      </span>
      <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
        {!done && both && battle.status === 'live' && (
          <SmallBtn disabled={busy} onClick={() => act(`/api/admin/battles/${battle.id}/focus`, { match_id: match.id })}>Show on stream</SmallBtn>
        )}
        <SmallBtn onClick={onOpen}>Open</SmallBtn>
      </div>
    </div>
  );
}

// One match with its admin controls. A fresh roll plays the same reel as the
// public card (the console is often what's on stream while rolling); the
// per-play controls and the forfeit buttons, which carry the players' names,
// appear once it lands. Callers key this on match.id.
export function MatchConsole({ match, state, act, busy, labels, extra }) {
  const { battle } = state;
  const clock = useServerClock(state);
  const onStream = battle.focus_match_id === match.id;
  const { a, b } = sidesOf(match);
  const bothSides = a.length && b.length;
  const picking = battle.format === 'tournament' && battle.picking_round === match.round;
  return (
    <FightCard
      match={match}
      labels={labels}
      clock={clock}
      pool={state.entries}
      renderPlay={(p) => (picking && p.buy == null ? null : <PlayControls play={p} match={match} battle={battle} act={act} busy={busy} />)}
      header={({ rolling }) => (
        <div style={{ marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem' }}>
          {onStream ? (
            <span style={{ ...eyebrow, color: C.lav, display: 'flex', alignItems: 'center', gap: '0.4rem' }}><LiveDot size={6} /> on stream</span>
          ) : (
            <SmallBtn disabled={busy || !bothSides} onClick={() => act(`/api/admin/battles/${battle.id}/focus`, { match_id: match.id })}>Show on stream</SmallBtn>
          )}
          {match.status === 'pending' && bothSides && !picking && !rolling && (
            <>
              <SmallBtn disabled={busy} onClick={() => window.confirm(`${labels.a} goes through without playing?`) && act(`/api/admin/battles/matches/${match.id}/forfeit`, { winner_side: 'a' })}>
                Forfeit → {labels.a}
              </SmallBtn>
              <SmallBtn disabled={busy} onClick={() => window.confirm(`${labels.b} goes through without playing?`) && act(`/api/admin/battles/matches/${match.id}/forfeit`, { winner_side: 'b' })}>
                Forfeit → {labels.b}
              </SmallBtn>
            </>
          )}
          {match.status === 'done' && match.forfeit && (
            <SmallBtn disabled={busy} onClick={() => act(`/api/admin/battles/matches/${match.id}/undo`)}>Undo forfeit</SmallBtn>
          )}
          {extra}
        </div>
      )}
    />
  );
}

// ---------- tournament ----------

function PickWindow({ state, act, busy }) {
  const { battle } = state;
  const round = battle.picking_round;
  const alive = playersInPlay(state);
  const picked = alive.filter((p) => (p.picked_round ?? 0) >= round);
  const [editing, setEditing] = useState(null);
  const pendingPlay = (playerId) => state.matches
    .filter((m) => m.status === 'pending' && m.round === round)
    .flatMap((m) => m.plays)
    .find((p) => p.player_id === playerId);

  return (
    <div style={{ marginTop: '1.25rem', borderRadius: '16px', border: '1px solid rgba(168,85,247,0.4)', background: 'rgba(168,85,247,0.05)', padding: '1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <span style={{ ...eyebrow, color: C.lav }}>{roundLabel(round, battle.bracket_size)} · picks open</span>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#fff' }}>
            {picked.length} of {alive.length} survivors have picked. The widget is showing who still owes one.
          </p>
        </div>
        <Btn kind="primary" disabled={busy} onClick={() => act(`/api/admin/battles/${battle.id}/start-round`)}>
          Start {roundLabel(round, battle.bracket_size).toLowerCase()}
        </Btn>
      </div>
      <Hint>Anyone who hasn&apos;t picked when you start keeps the slot they played last round.</Hint>
      <div className="sb-grid sb-3" style={{ marginTop: '0.75rem' }}>
        {alive.map((p) => {
          const done = (p.picked_round ?? 0) >= round;
          const play = pendingPlay(p.id);
          return (
            <div key={p.id} style={{ borderRadius: '12px', border: `1px solid ${done ? 'rgba(74,222,128,0.35)' : C.line}`, padding: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <SlotArt slot={p.slot} width={28} radius={4} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: 'block', fontSize: '0.84rem', fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  <span style={{ display: 'block', fontSize: '0.68rem', color: done ? C.mint : C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {done ? p.slot?.name || p.slot?.raw : `waiting — last: ${p.slot?.name || p.slot?.raw || '—'}`}
                  </span>
                </div>
                {play && <SmallBtn onClick={() => setEditing(editing === p.id ? null : p.id)}>Set</SmallBtn>}
              </div>
              {editing === p.id && play && (
                <div style={{ marginTop: '0.5rem' }}>
                  <SlotSearch
                    autoFocus
                    onPick={async ({ slot, text }) => {
                      const ok = await act(`/api/admin/battles/plays/${play.id}/pick`, slot ? { slot_key: slot.key } : { text });
                      if (ok) setEditing(null);
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PrizeEditor({ battle, act, busy }) {
  const [prizes, setPrizes] = useState(() => (battle.prizes.length ? battle.prizes.map(String) : ['']));
  return (
    <div style={{ marginTop: '1rem' }}>
      <Label>prizes by placement</Label>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.5rem' }}>
        <PrizeInputs prizes={prizes} setPrizes={setPrizes} max={battle.bracket_size} />
        <SmallBtn disabled={busy} onClick={() => act(`/api/admin/battles/${battle.id}`, { prizes: prizes.map((p) => Number(p) || 0) }, 'PUT')}>Save prizes</SmallBtn>
      </div>
    </div>
  );
}

function PayoutTable({ state, act, busy }) {
  const { battle, players } = state;
  const accounts = useContext(BattleAccountsContext);
  const rows = players
    .filter((p) => !p.replaced && (p.prize_amount > 0 || p.placement))
    .sort((x, y) => (x.placement ?? 999) - (y.placement ?? 999) || x.id - y.id)
    .filter((p) => battle.format !== 'tournament' || p.placement <= Math.max(3, battle.prizes.length));
  // Typed amounts that differ from what's saved. "Mark paid" saves these
  // first, so a typed bump without pressing Save isn't paid at the old amount.
  const [draft, setDraft] = useState({});
  const edited = (p) => !p.awarded && draft[p.id] != null && String(draft[p.id]) !== String(p.prize_amount ?? '');
  const amountOf = (p) => (edited(p) ? Number(draft[p.id]) || 0 : p.prize_amount || 0);
  const unpaid = rows.filter((p) => !p.awarded && amountOf(p) > 0);
  const unpaidTotal = unpaid.reduce((s, p) => s + amountOf(p), 0);
  const unsaved = rows.filter(edited);
  if (!rows.length) return null;

  const save = async (p) => {
    const ok = await act(`/api/admin/battles/players/${p.id}/prize`, { amount: draft[p.id] }, 'PUT');
    if (ok) setDraft((d) => { const n = { ...d }; delete n[p.id]; return n; });
    return ok;
  };

  const award = async () => {
    const list = unpaid.map((p) => `  ${p.name}${accounts[p.id]?.shuffle ? ` (Shuffle: ${accounts[p.id].shuffle})` : ''}: ${fmtAmount(amountOf(p))}`).join('\n');
    if (!window.confirm(`Mark ${unpaid.length} prize${unpaid.length === 1 ? '' : 's'} as paid (${fmtAmount(unpaidTotal)})?\n\n${list}\n\nSend the money first — paid prizes land in the rewards ledger and can't be changed after.`)) return;
    for (const p of unsaved) {
      if (!(await save(p))) return; // the error shows; nothing is marked
    }
    await act(`/api/admin/battles/${battle.id}/award`);
  };

  return (
    <div style={{ marginTop: '1.25rem' }}>
      <Label>prizes</Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {rows.map((p) => (
          <div key={p.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 0.75rem', borderRadius: '12px', border: `1px solid ${C.line}`, background: 'rgba(0,0,0,0.22)', padding: '0.45rem 0.75rem' }}>
            {p.placement && <span style={{ ...eyebrow, width: '2.5rem', fontSize: '0.62rem', color: p.placement === 1 ? C.lav : C.dim }}>{ordinal(p.placement)}</span>}
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: 'block', fontSize: '0.84rem', fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
              <PlayerAccount playerId={p.id} />
            </span>
            {p.awarded ? (
              <span style={{ fontSize: '0.84rem', fontWeight: 900, color: C.mint }}>{fmtAmount(p.prize_amount)} <span style={{ ...eyebrow, fontSize: '0.54rem', color: C.mint }}>paid</span></span>
            ) : (
              <form style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }} onSubmit={(e) => { e.preventDefault(); if (edited(p)) save(p); }}>
                <input
                  type="number" min="0" step="0.01"
                  value={draft[p.id] ?? (p.prize_amount ?? '')}
                  onChange={(e) => setDraft((d) => ({ ...d, [p.id]: e.target.value }))}
                  style={{ ...field, width: '96px', padding: '0.3rem 0.45rem', fontSize: '0.74rem', borderColor: edited(p) ? 'rgba(168,85,247,0.6)' : undefined }}
                />
                {edited(p) && (
                  <>
                    <span style={{ ...eyebrow, fontSize: '0.54rem', color: C.lav }}>unsaved</span>
                    <button type="submit" className="sb-btn" disabled={busy} style={btn('ghost', 'sm')}>Save</button>
                  </>
                )}
              </form>
            )}
          </div>
        ))}
      </div>
      {unpaid.length > 0 && (battle.format !== 'tournament' || battle.status === 'finished') && (
        <Btn kind="green" style={{ marginTop: '0.75rem' }} disabled={busy} onClick={award}>
          {unsaved.length ? 'Save & mark' : 'Mark'} {unpaid.length} prize{unpaid.length === 1 ? '' : 's'} paid · {fmtAmount(unpaidTotal)}
        </Btn>
      )}
    </div>
  );
}

// First-round matches that haven't been rolled yet (no plays) — before the
// first roll the bracket doesn't exist, so every one of them is still to come.
const unrolledCount = (state) => (state.battle.status === 'lobby'
  ? state.battle.bracket_size / 2
  : state.matches.filter((m) => m.round === 1 && m.plays.length === 0).length);

// Roll the next first-round match from the pool: both players at once, on
// stream. The pool stays open until the last seat is filled.
function RollNext({ state, admin, act, busy }) {
  const { battle } = state;
  const eligible = admin?.eligible_count ?? state.entry_count;
  const firstRound = battle.bracket_size / 2;
  const left = unrolledCount(state);
  const next = firstRound - left + 1;
  return (
    <div style={{ marginTop: '1rem', borderRadius: '16px', border: '1px solid rgba(168,85,247,0.3)', background: 'rgba(168,85,247,0.04)', padding: '1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.6rem' }}>
        <Btn kind="primary" size="lg" disabled={busy || eligible < 2} onClick={() => act(`/api/admin/battles/${battle.id}/matches`)}>
          🎲 Roll match {next} of {firstRound}
        </Btn>
        <Btn disabled={busy} onClick={() => act(`/api/admin/battles/${battle.id}/entries`, { open: !battle.entries_open })}>
          {battle.entries_open ? 'Lock entries' : 'Reopen entries'}
        </Btn>
        <SiteOnlySelect battle={battle} act={act} busy={busy} />
        {battle.status === 'lobby' && (
          <>
            <select value={battle.bracket_size} disabled={busy} style={{ ...field, width: 'auto', padding: '0.45rem 0.6rem' }}
              onChange={(e) => act(`/api/admin/battles/${battle.id}`, { bracket_size: Number(e.target.value) }, 'PUT')}>
              {[4, 8, 16, 32, 64].map((n) => <option key={n} value={n}>{n} seats</option>)}
            </select>
            <select value={battle.third_place ? 'match' : 'multi'} disabled={busy} style={{ ...field, width: 'auto', padding: '0.45rem 0.6rem' }}
              onChange={(e) => act(`/api/admin/battles/${battle.id}`, { third_place: e.target.value === 'match' }, 'PUT')}>
              <option value="match">3rd place match</option>
              <option value="multi">3rd by semi-final multi</option>
            </select>
          </>
        )}
      </div>
      <Hint>
        Rolls two players from the pool into the next first-round match and puts it on stream.{' '}
        <strong style={{ color: '#fff' }}>{eligible}</strong> in the pool who haven&apos;t been rolled
        {battle.site_only ? ' (with a site account)' : ''}
        {eligible < 2 ? ' — waiting for more entries' : ''}. {left - 1 > 0 ? `${left - 1} more to roll after this one.` : 'This fills the bracket and closes the pool.'}
      </Hint>
      <Hint style={{ marginTop: '0.2rem' }}>
        Until they&apos;re rolled, viewers can join late, retype {battle.command} with another slot to change their pick, or type {EXIT_COMMAND} to leave.
        {battle.status === 'lobby' ? ' Seat count, 3rd place and re-pick rule lock on the first roll.' : ''}
      </Hint>
    </div>
  );
}

// Earlier fights listed before "All N" — enough to fix a recent mistake.
const RECENT_FIGHTS = 6;

// Matches opened out of their one-line row by hand.
function useOpened() {
  const [opened, setOpened] = useState(() => new Set());
  const toggle = useCallback((id) => setOpened((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }), []);
  return [opened, toggle];
}

function TournamentConsole({ state, admin, act, busy }) {
  const { battle, matches } = state;
  const [showAll, setShowAll] = useState(false);
  const [opened, toggle] = useOpened();
  const left = unrolledCount(state);

  if (battle.status === 'lobby') {
    return (
      <>
        <RollNext state={state} admin={admin} act={act} busy={busy} />
        <Hint style={{ marginTop: '0.75rem' }}>
          {battle.bracket_size}-seat bracket · picks are {battle.repick === 'each_round' ? 're-made every round' : 'kept all the way'}
          {' · '}{battle.third_place ? 'a 3rd place match before the final' : '3rd goes to the better semi-final multi'}.
        </Hint>
        <PrizeEditor battle={battle} act={act} busy={busy} />
      </>
    );
  }

  const round = battle.current_round;
  const rolled = (m) => m.plays.length > 0;
  const current = matches.filter((m) => m.round === round && rolled(m));
  const stragglers = matches.filter((m) => m.round < round && m.status !== 'done');
  // what's left to play first, the one on stream at the very top — and the
  // 3rd-place match ahead of the final, the order it's meant to be played in
  const order = (m) => (m.id === battle.focus_match_id ? 0
    : m.status === 'pending' ? (isThirdPlace(m, battle) ? 1 : 1.5) : 2);
  const shown = (showAll ? matches.filter(rolled) : [...stragglers, ...current]).slice().sort((x, y) => order(x) - order(y));
  const unroll = (m) => m.round === 1 && m.status === 'pending' && !m.plays.some((p) => p.buy != null);

  return (
    <>
      {battle.status === 'live' && left > 0 && <RollNext state={state} admin={admin} act={act} busy={busy} />}
      <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        {battle.status === 'live' && (
          <Btn disabled={busy || !battle.focus_match_id} onClick={() => act(`/api/admin/battles/${battle.id}/focus`, { match_id: null })}>
            Show the round board on stream
          </Btn>
        )}
        {battle.status === 'live' && battle.repick === 'each_round' && !battle.picking_round && round > 1 && (
          <SmallBtn disabled={busy} onClick={() => act(`/api/admin/battles/${battle.id}/reopen-picks`)}>Reopen picks</SmallBtn>
        )}
        <SmallBtn onClick={() => setShowAll((v) => !v)}>{showAll ? 'Current round only' : 'Every match'}</SmallBtn>
      </div>
      {battle.status === 'live' && battle.picking_round && <PickWindow state={state} act={act} busy={busy} />}
      {battle.status === 'live' && (
        <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {shown.map((m) => {
            const labels = tournamentLabels(m, battle);
            const onStream = m.id === battle.focus_match_id;
            if (!onStream && !opened.has(m.id)) {
              return <MatchRow key={m.id} match={m} battle={battle} labels={labels} heading={labels.heading} act={act} busy={busy} onOpen={() => toggle(m.id)} />;
            }
            return (
              <div key={m.id} style={{ margin: '0.5rem 0' }}>
                <span style={{ ...eyebrow, fontSize: '0.6rem' }}>{labels.heading}</span>
                <div style={{ marginTop: '0.35rem' }}>
                  <MatchConsole
                    match={m}
                    state={state}
                    act={act}
                    busy={busy}
                    labels={labels}
                    extra={(
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.35rem' }}>
                        {unroll(m) && (
                          <SmallBtn disabled={busy} onClick={() => window.confirm('Un-roll this match? Both players go back in the pool and the seat is empty again.') && act(`/api/admin/battles/matches/${m.id}`, null, 'DELETE')}>
                            Un-roll
                          </SmallBtn>
                        )}
                        {!onStream && <SmallBtn onClick={() => toggle(m.id)}>Fold</SmallBtn>}
                      </span>
                    )}
                  />
                </div>
              </div>
            );
          })}
          {!shown.length && <p style={{ fontSize: '0.84rem', color: C.dim }}>Roll the first match to start the bracket.</p>}
        </div>
      )}
      {battle.status === 'finished' && (
        <Hint style={{ marginTop: '1rem', fontSize: '0.8rem' }}>
          Bracket finished. Clearing a result in the final (open it from the bracket) reopens it — until prizes are marked paid.
        </Hint>
      )}
      <PayoutTable state={state} act={act} busy={busy} />
    </>
  );
}

// ---------- arena / versus ----------

// Versus 2v2/3v3: the streamer chooses his teammates from the pool (not
// rolled for chat yet, not shadowbanned, site accounts only if the battle says
// so). Teammates go back in the pool after their fight, and this session's
// come first (most fights together), so picking the same people again is one
// click.
function TeammatePicker({ entries, chosen, seats, onChange, streamer }) {
  const [q, setQ] = useState('');
  const left = seats - chosen.length;
  const needle = q.trim().toLowerCase();
  const matches = entries
    .filter((e) => !chosen.some((c) => c.id === e.id))
    .filter((e) => !needle || e.name.toLowerCase().includes(needle))
    .sort((x, y) => (y.teammate_fights || 0) - (x.teammate_fights || 0))
    .slice(0, 8);
  return (
    <div style={{ marginTop: '0.75rem', borderRadius: '12px', border: `1px solid ${C.line}`, background: 'rgba(255,255,255,0.03)', padding: '0.75rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ ...eyebrow, fontSize: '0.56rem' }}>{streamer}&apos;s teammates</span>
        {chosen.map((e) => (
          <span key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', borderRadius: '999px', border: '1px solid rgba(168,85,247,0.5)', background: 'rgba(168,85,247,0.1)', padding: '2px 8px 2px 3px', fontSize: '0.8rem', color: '#fff' }}>
            <SlotArt slot={e.slot} width={18} radius={3} />
            {e.name}
            <button type="button" aria-label={`Remove ${e.name}`} onClick={() => onChange(chosen.filter((c) => c.id !== e.id))} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '0.9rem' }}>×</button>
          </span>
        ))}
        {left > 0 && (
          <span style={{ fontSize: '0.7rem', color: C.dim }}>
            {left} seat{left > 1 ? 's' : ''} open — pick {chosen.length ? 'another' : 'someone'}, or leave {left > 1 ? 'them' : 'it'} to a random roll
          </span>
        )}
      </div>
      {left > 0 && (
        <>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${entries.length} in the pool…`} style={{ ...field, marginTop: '0.5rem', padding: '0.4rem 0.6rem' }} />
          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {matches.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => { onChange([...chosen, e]); setQ(''); }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', borderRadius: '9px', border: `1px solid ${C.line}`, background: 'transparent', padding: '0.25rem 0.5rem', cursor: 'pointer', fontFamily: C.font, fontSize: '0.8rem', color: '#fff', textAlign: 'left' }}
              >
                <SlotArt slot={e.slot} width={18} radius={3} />
                <span>{e.name}</span>
                <span style={{ maxWidth: '9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.66rem', color: C.muted }}>{e.slot?.name || e.slot?.raw || 'no pick'}</span>
                {e.teammate_fights > 0 && (
                  <span title="Fights on your side this session" style={{ ...eyebrow, fontSize: '0.5rem', color: C.lav }}>teammate ×{e.teammate_fights}</span>
                )}
              </button>
            ))}
            {!matches.length && <span style={{ fontSize: '0.7rem', color: C.dim }}>Nobody in the pool matches.</span>}
          </div>
        </>
      )}
    </div>
  );
}

function FightConsole({ state, admin, act, busy }) {
  const { battle, matches } = state;
  const eligible = admin?.eligible_count ?? state.entry_count;
  // the session's defaults, overridable per fight
  const [teamSize, setTeamSize] = useState(battle.default_team_size || 1);
  const [prize, setPrize] = useState(battle.default_prize ?? '');
  const [house, setHouse] = useState(null); // { slot } | { text }
  // versus 2v2/3v3: the streamer's handpicked teammates (pool entries)
  const [mates, setMates] = useState([]);
  const versus = battle.format === 'versus';
  const seats = versus ? teamSize - 1 : 0;
  const chosen = mates.slice(0, seats);
  const pending = matches.find((m) => m.status === 'pending');
  // everyone in the fight but the streamer comes out of the pool
  const need = versus ? teamSize * 2 - 1 : teamSize * 2;
  const decided = matches.filter((m) => m.status === 'done').slice().reverse();
  const [opened, toggle] = useOpened();
  const [allFights, setAllFights] = useState(false);

  return (
    <>
      <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        <Btn disabled={busy} onClick={() => act(`/api/admin/battles/${battle.id}/entries`, { open: !battle.entries_open })}>
          {battle.entries_open ? 'Lock entries' : 'Reopen entries'}
        </Btn>
        <SiteOnlySelect battle={battle} act={act} busy={busy} />
        <span style={{ fontSize: '0.74rem', color: C.dim }}>
          {eligible} in the pool who haven&apos;t played yet{battle.site_only ? ' (with a site account)' : ''}
        </span>
      </div>

      {pending ? (
        <div style={{ marginTop: '1.25rem' }}>
          <MatchConsole
            key={pending.id}
            match={pending}
            state={state}
            act={act}
            busy={busy}
            labels={fightLabels(pending, battle)}
            extra={(
              <SmallBtn
                style={{ marginLeft: 'auto' }}
                disabled={busy || pending.plays.some((p) => p.buy != null)}
                onClick={() => window.confirm('Cancel this fight? Its players go back in the pool.') && act(`/api/admin/battles/matches/${pending.id}`, null, 'DELETE')}
              >
                Cancel fight
              </SmallBtn>
            )}
          />
        </div>
      ) : (
        <div style={{ marginTop: '1.25rem', borderRadius: '16px', border: `1px solid ${C.line}`, background: 'rgba(0,0,0,0.25)', padding: '1rem' }}>
          <Label>next fight</Label>
          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.75rem' }}>
            <div>
              <span style={{ ...eyebrow, fontSize: '0.56rem' }}>format</span>
              <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.35rem' }}>
                {[1, 2, 3].map((n) => <Choice key={n} on={teamSize === n} onClick={() => setTeamSize(n)}>{n}v{n}</Choice>)}
              </div>
            </div>
            <label style={{ width: '130px' }}>
              <span style={{ ...eyebrow, fontSize: '0.56rem' }}>prize this fight ($)</span>
              <input type="number" min="0" value={prize} onChange={(e) => setPrize(e.target.value)} style={{ ...field, marginTop: '0.25rem' }} />
            </label>
            {versus && (
              <div style={{ minWidth: '16rem', flex: 1 }}>
                <span style={{ ...eyebrow, fontSize: '0.56rem' }}>{battle.streamer_name}&apos;s pick</span>
                <div style={{ marginTop: '0.25rem' }}>
                  {house ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderRadius: '10px', border: `1px solid ${C.line}`, background: 'rgba(255,255,255,0.04)', padding: '0.3rem 0.5rem' }}>
                      <SlotArt slot={house.slot || { raw: house.text }} width={26} radius={4} />
                      <span style={{ flex: 1, fontSize: '0.84rem', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{house.slot?.name || house.text}</span>
                      <SmallBtn onClick={() => setHouse(null)}>Change</SmallBtn>
                    </div>
                  ) : (
                    <SlotSearch onPick={setHouse} placeholder="Search his slot…" />
                  )}
                </div>
              </div>
            )}
            <Btn
              kind="primary"
              size="lg"
              disabled={busy || eligible < need}
              onClick={async () => {
                const ok = await act(`/api/admin/battles/${battle.id}/matches`, {
                  team_size: teamSize,
                  prize_amount: prize,
                  streamer_slot_key: house?.slot?.key,
                  streamer_pick_raw: house?.text,
                  teammate_entry_ids: versus ? chosen.map((e) => e.id) : undefined,
                });
                if (ok) { setHouse(null); setMates([]); }
              }}
            >
              🎲 {versus
                ? (teamSize === 1 ? 'Roll a viewer from chat' : `Roll ${teamSize} viewers vs your team`)
                : `Roll ${teamSize}v${teamSize} from chat`}
            </Btn>
          </div>
          {versus && teamSize > 1 && (
            <TeammatePicker entries={admin?.eligible_entries || []} chosen={chosen} seats={seats} onChange={setMates} streamer={battle.streamer_name} />
          )}
          {battle.format === 'arena' && (
            <Hint>
              {need} players rolled, split into two teams. You buy all {need} bonuses; the higher average multi wins
              {teamSize > 1 ? ` and the prize splits ${teamSize} ways` : ''}.
            </Hint>
          )}
          {versus && teamSize > 1 && (
            <Hint>
              {battle.streamer_name} + {seats} teammate{seats > 1 ? 's' : ''} vs {teamSize} viewers rolled from chat — you buy all {teamSize * 2} bonuses, the higher
              average multi wins. A seat you leave empty is filled at random from the pool. Teammates go back in the pool after the fight, and can retype the
              command to change their slot until their bonus is bought. The prize is only paid if chat wins (split {teamSize} ways).
            </Hint>
          )}
          {eligible < need && <Hint style={{ color: C.loss }}>Need {need} in the pool — {eligible} left.</Hint>}
        </div>
      )}

      {decided.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
            <Label style={{ marginBottom: 0 }}>played this session · {decided.length}</Label>
            {decided.length > RECENT_FIGHTS && (
              <SmallBtn onClick={() => setAllFights((v) => !v)}>{allFights ? 'Latest only' : `All ${decided.length}`}</SmallBtn>
            )}
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {(allFights ? decided : decided.slice(0, RECENT_FIGHTS)).map((m) => {
              const labels = fightLabels(m, battle);
              const heading = versus
                ? `round ${m.round}${m.team_size > 1 ? ` · ${m.team_size}v${m.team_size}` : ''}`
                : `fight ${m.round} · ${m.team_size}v${m.team_size}`;
              return opened.has(m.id) ? (
                <MatchConsole key={m.id} match={m} state={state} act={act} busy={busy} labels={labels}
                  extra={<SmallBtn style={{ marginLeft: 'auto' }} onClick={() => toggle(m.id)}>Fold</SmallBtn>} />
              ) : (
                <MatchRow key={m.id} match={m} battle={battle} labels={labels} heading={heading} act={act} busy={busy} onOpen={() => toggle(m.id)} />
              );
            })}
          </div>
        </div>
      )}

      <PayoutTable state={state} act={act} busy={busy} />
    </>
  );
}

// ---------- shell ----------

export default function BattleConsole({ state, reload, defaultFormat }) {
  const b = state?.battle;
  const live = isLive(b);
  const [admin, setAdmin] = useState(null);
  const timer = useRef(null);

  // The feed carries the public view; the eligible pool and site accounts
  // only come from the admin route. Refetched (debounced) when the feed moves.
  const refreshAdmin = useCallback(() => {
    if (!b?.id) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => adminBattle(b.id).then(setAdmin).catch(() => {}), 300);
  }, [b?.id]);
  useEffect(() => { refreshAdmin(); }, [state, refreshAdmin]);
  // never show one battle's pool count against another
  useEffect(() => { setAdmin(null); }, [b?.id]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onDone = useCallback(() => { if (reload) reload(); refreshAdmin(); }, [reload, refreshAdmin]);
  const { busy, error, act } = useAct(onDone);
  const focus = useMemo(() => focusedMatch(state), [state]);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // a new battle closes the form again for the next time one finishes
  useEffect(() => { setCreating(false); }, [b?.id]);

  return (
    <BattleAccountsContext.Provider value={admin?.player_accounts || {}}>
      <BattlePoolContext.Provider value={admin?.eligible_entries || []}>
        <div style={{ ...panel, marginTop: '1.5rem', border: '1px solid rgba(168,85,247,0.3)', padding: '1.25rem 1.25rem 1.5rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 900, color: '#fff', fontFamily: C.display }}>
              🎛️ Battle controls{live ? ` · ${FORMAT_META[b.format].label}` : ''}
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem' }}>
              <SmallBtn active={widgetOpen} onClick={() => setWidgetOpen((v) => !v)}>📺 OBS widget {widgetOpen ? '▴' : '▾'}</SmallBtn>
              {live && (
                <>
                  {b.format !== 'tournament' && (
                    <SmallBtn disabled={busy} onClick={() => window.confirm('End this session? Unpaid prizes can still be marked paid after.') && act(`/api/admin/battles/${b.id}/finish`)}>
                      Finish session
                    </SmallBtn>
                  )}
                  <SmallBtn
                    disabled={busy}
                    style={{ color: '#fca5a5' }}
                    onClick={() => window.confirm('Scrap this battle? It stays in the record as cancelled.') && act(`/api/admin/battles/${b.id}/cancel`)}
                  >
                    Cancel battle
                  </SmallBtn>
                </>
              )}
            </div>
          </div>

          {/* set up once in OBS, then never needed mid-battle — folded away */}
          {widgetOpen && <StreamWidgetCard />}

          {error && <ErrorBox>{error}</ErrorBox>}

          {live ? (
            <>
              <Hint style={{ fontSize: '0.76rem' }}>
                Chat enters with <strong style={{ color: '#fff' }}>{b.command} &lt;slot&gt;</strong> — picks are matched to the Shuffle catalog as they come
                in; fix any wrong match from its card.{focus ? ' The widget is showing the highlighted match.' : ''}
              </Hint>
              {b.format === 'tournament'
                ? <TournamentConsole state={state} admin={admin} act={act} busy={busy} />
                : <FightConsole state={state} admin={admin} act={act} busy={busy} />}
            </>
          ) : (
            <>
              {b?.status === 'finished' && (
                <>
                  <Hint style={{ fontSize: '0.76rem' }}>Last battle: {FORMAT_META[b.format].label}{b.title ? ` — ${b.title}` : ''}.</Hint>
                  <PayoutTable state={state} act={act} busy={busy} />
                  {state.players.some((p) => p.prize_amount > 0 && !p.awarded) && (
                    <Hint>Mark prizes paid before starting the next one, or find the battle again later in its history entry.</Hint>
                  )}
                </>
              )}
              {/* right after a battle the job is paying it out; the create form
                  waits behind a button instead of pushing the page down */}
              {b?.status === 'finished' && !creating ? (
                <Btn kind="primary" style={{ marginTop: '1.25rem' }} onClick={() => setCreating(true)}>Start a new battle</Btn>
              ) : (
                <CreateBattle onDone={onDone} defaultFormat={defaultFormat} />
              )}
            </>
          )}
        </div>
      </BattlePoolContext.Provider>
    </BattleAccountsContext.Provider>
  );
}

// Admin controls for one match, opened from the bracket (any round, finished
// brackets included — clearing a result there is how a bracket reopens).
export function AdminMatch({ match, state, onDone }) {
  const { busy, error, act } = useAct(onDone);
  const [admin, setAdmin] = useState(null);
  const battleId = state.battle.id;
  useEffect(() => { adminBattle(battleId).then(setAdmin).catch(() => {}); }, [battleId]);
  return (
    <BattleAccountsContext.Provider value={admin?.player_accounts || {}}>
      <BattlePoolContext.Provider value={admin?.eligible_entries || []}>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div style={{ marginTop: error ? '0.75rem' : 0 }}>
          <MatchConsole match={match} state={state} act={act} busy={busy} labels={fightLabels(match, state.battle)} />
        </div>
      </BattlePoolContext.Provider>
    </BattleAccountsContext.Provider>
  );
}

// Prize table + mark-paid for any battle, e.g. a past one opened from history.
export function AdminPayouts({ state, onDone }) {
  const { busy, error, act } = useAct(onDone);
  const [admin, setAdmin] = useState(null);
  const battleId = state.battle.id;
  useEffect(() => { adminBattle(battleId).then(setAdmin).catch(() => {}); }, [battleId]);
  return (
    <BattleAccountsContext.Provider value={admin?.player_accounts || {}}>
      {error && <ErrorBox>{error}</ErrorBox>}
      <PayoutTable state={state} act={act} busy={busy} />
    </BattleAccountsContext.Provider>
  );
}

// for the history modal's admin delete
export function useBattleDelete(onDone) {
  const { busy, error, act } = useAct(onDone);
  return { busy, error, del: (id) => act(`/api/admin/battles/${id}`, null, 'DELETE') };
}

